import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { NavLink as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { styled, alpha } from '@mui/material/styles';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import Iconify from '../../components/iconify';
import Logo from '../../components/logo/Logo';
import { StyledNavItem, StyledNavItemIcon } from '../../components/nav-section/styles';
import useResponsive from '../../hooks/useResponsive';
import { bgBlur } from '../../utils/cssStyles';
import api from '../../api';

const NAV_WIDTH = 280;
const HEADER_MOBILE = 64;
const HEADER_DESKTOP = 92;

const NAV_CONFIG = [
  {
    title: 'Dashboard',
    path: '/client',
    end: true,
    icon: <Iconify icon="solar:widget-5-bold-duotone" width={22} />,
  },
  {
    title: 'My packages',
    path: '/client/packages',
    icon: <Iconify icon="mdi:package-variant-closed" width={22} />,
  },
  {
    title: 'My bills',
    path: '/client/bills',
    icon: <Iconify icon="solar:bill-list-bold" width={22} />,
  },
  {
    title: 'Password',
    path: '/client/password',
    icon: <Iconify icon="mdi:account-key" width={22} />,
  },
];

const StyledRoot = styled('div')({
  display: 'flex',
  height: '100dvh',
  minHeight: '100vh',
  overflow: 'hidden',
});

const StyledHeader = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'isDesktopNavOpen',
})(({ theme, isDesktopNavOpen }) => ({
  ...bgBlur({ color: theme.palette.background.default }),
  boxShadow: 'none',
  [theme.breakpoints.down('lg')]: {
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
    backgroundImage: `linear-gradient(
      180deg,
      ${alpha('#FFFFFF', 0.58)} 0%,
      ${alpha('#FFFFFF', 0.14)} 55%,
      ${alpha('#FFFFFF', 0.04)} 100%
    )`,
    backdropFilter: 'saturate(190%) blur(28px)',
    WebkitBackdropFilter: 'saturate(190%) blur(28px)',
    borderBottom: `1px solid ${alpha('#FFFFFF', 0.45)}`,
    boxShadow: `0 8px 28px ${alpha(theme.palette.grey[900], 0.08)}, inset 0 -1px 0 ${alpha('#FFFFFF', 0.35)}`,
  },
  [theme.breakpoints.up('lg')]: {
    width: isDesktopNavOpen ? `calc(100% - ${NAV_WIDTH + 1}px)` : '100%',
  },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: HEADER_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: HEADER_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  minWidth: 0,
  height: '100dvh',
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch',
  paddingTop: HEADER_MOBILE + 16,
  paddingBottom: theme.spacing(4),
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  [theme.breakpoints.up('lg')]: {
    paddingTop: HEADER_DESKTOP + 24,
    paddingBottom: theme.spacing(10),
  },
}));

const StyledAccount = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

function SideNav({ open, onClose, isDesktopNavOpen, user, onLogout }) {
  const isDesktop = useResponsive('up', 'lg');

  const content = (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ px: 2.5, py: 3, display: 'inline-flex' }}>
        <Logo />
      </Box>

      <Box sx={{ mb: 5, mx: 2.5 }}>
        <StyledAccount>
          <Avatar src="/assets/images/avatars/avatar_default.jpg" alt="photoURL" />
          <Box sx={{ ml: 2, minWidth: 0 }}>
            <Typography variant="subtitle2" noWrap sx={{ color: 'text.primary' }}>
              {user?.name || 'Client'}
            </Typography>
            <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
              {user?.email || 'Billing portal'}
            </Typography>
          </Box>
        </StyledAccount>
      </Box>

      <List disablePadding sx={{ p: 1, flexGrow: 1 }}>
        {NAV_CONFIG.map((item) => (
          <StyledNavItem
            key={item.path}
            component={RouterLink}
            to={item.path}
            end={Boolean(item.end)}
            onClick={onClose}
            sx={{
              mb: 0.5,
              '&.active': {
                color: 'primary.main',
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.14),
                fontWeight: 'fontWeightBold',
              },
            }}
          >
            <StyledNavItemIcon>{item.icon}</StyledNavItemIcon>
            <ListItemText disableTypography primary={item.title} />
          </StyledNavItem>
        ))}
      </List>

      <Box sx={{ px: 2.5, pb: 3, pt: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          color="inherit"
          onClick={onLogout}
          startIcon={<Iconify icon="eva:log-out-outline" />}
        >
          Logout
        </Button>
      </Box>
    </Box>
  );

  if (isDesktop) {
    return (
      <Box
        component="nav"
        sx={{
          flexShrink: 0,
          width: isDesktopNavOpen ? NAV_WIDTH : 0,
          display: 'block',
        }}
      >
        <Drawer
          open={isDesktopNavOpen}
          variant="persistent"
          PaperProps={{
            sx: {
              width: NAV_WIDTH,
              height: '100vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'background.default',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {content}
        </Drawer>
      </Box>
    );
  }

  return (
    <Drawer open={open} onClose={onClose} PaperProps={{ sx: { width: NAV_WIDTH } }}>
      {content}
    </Drawer>
  );
}

SideNav.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  isDesktopNavOpen: PropTypes.bool,
  user: PropTypes.object,
  onLogout: PropTypes.func,
};

export default function ClientLayout() {
  const [openMobileNav, setOpenMobileNav] = useState(false);
  const [openDesktopNav, setOpenDesktopNav] = useState(true);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || 'null');

  useEffect(() => {
    setOpenMobileNav(false);
  }, [pathname]);

  const logout = () => {
    api
      .delete('/sessions')
      .catch(() => null)
      .finally(() => {
        localStorage.clear();
        navigate('/login');
        toast.success('Logged out');
      });
  };

  return (
    <StyledRoot>
      <StyledHeader isDesktopNavOpen={openDesktopNav}>
        <StyledToolbar>
          <IconButton
            onClick={() => setOpenMobileNav(true)}
            sx={{
              mr: 1,
              color: 'text.primary',
              display: { lg: 'none' },
              bgcolor: (theme) => alpha(theme.palette.background.paper, 0.4),
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              border: (theme) => `1px solid ${alpha('#fff', 0.45)}`,
              '&:hover': {
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.55),
              },
            }}
          >
            <Iconify icon="eva:menu-2-fill" />
          </IconButton>

          <IconButton
            onClick={() => setOpenDesktopNav((prev) => !prev)}
            sx={{
              mr: 1,
              color: 'text.primary',
              display: { xs: 'none', lg: 'inline-flex' },
            }}
          >
            <Iconify
              icon={openDesktopNav ? 'eva:arrow-ios-back-fill' : 'eva:arrow-ios-forward-fill'}
            />
          </IconButton>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="subtitle2"
              sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary' }}
            >
              Client portal
            </Typography>
            <Avatar
              src="/assets/images/avatars/avatar_default.jpg"
              alt={user?.name || 'Client'}
              sx={{ width: 36, height: 36 }}
            />
          </Stack>
        </StyledToolbar>
      </StyledHeader>

      <SideNav
        open={openMobileNav}
        onClose={() => setOpenMobileNav(false)}
        isDesktopNavOpen={openDesktopNav}
        user={user}
        onLogout={logout}
      />

      <Main>
        <Outlet />
      </Main>
    </StyledRoot>
  );
}
